/**
 * Deterministic per-page semantic content (paragraphs + FAQ JSON-LD) for
 * calendar Date / Month / Year pages. Avoids near-duplicate content that
 * Google would otherwise omit for ~thousands of programmatic pages.
 *
 * All output is build-time deterministic from existing datasets.
 */

import type { DayInfo } from "./info";
import {
  daysInMonth,
  dayOfWeek,
  MONTH_NAMES_ID,
} from "./dates";
import { gregorianToHijri, HIJRI_MONTHS } from "./hijri";
import { gregorianToChinese } from "./china";
import { getHolidaysForMonth, getHolidaysForYear } from "@/data/holidays";
import { getHariPentingForMonth, getAllHariPentingForYear } from "@/data/hari-penting";
import { getWesternZodiac } from "./zodiac";

export interface FaqItem {
  q: string;
  a: string;
}

export interface SemanticBlock {
  /** 2-4 sentences of unique copy for the page. */
  paragraphs: string[];
  /** FAQ items rendered as <h3>/<p> and as FAQPage JSON-LD. */
  faq: FaqItem[];
  /** schema.org "about" entities for entity surface widening. */
  about: { "@type": string; name: string }[];
}

/* ─────────────────────────────────────────────── DATE ── */

export function buildDateSemantics(info: DayInfo): SemanticBlock {
  const z = getWesternZodiac(info.month, info.day);
  const monthName = MONTH_NAMES_ID[info.month - 1];
  const fullDate = `${info.day} ${monthName} ${info.year}`;

  const liburParts = info.holidays
    .filter((h) => h.type === "libur")
    .map((h) => h.name);
  const cutiParts = info.holidays
    .filter((h) => h.type === "cuti-bersama")
    .map((h) => h.name);
  const hariPentingNames = info.hariPenting.map((h) => h.name);

  const paragraphs: string[] = [];

  // P1: identity
  paragraphs.push(
    `Tanggal ${fullDate} jatuh pada hari ${info.dayName}` +
      (info.isTanggalMerah ? " dan termasuk tanggal merah" : "") +
      `. Dalam penanggalan Hijriah bertepatan dengan ${info.hijriFormatted}, ` +
      `dan menurut penanggalan Jawa berstatus weton ${info.jawa.weton} ` +
      `(neptu ${info.jawa.neptu}, wuku ${info.jawa.wuku}).`,
  );

  // P2: zodiac + shio
  paragraphs.push(
    `Orang yang lahir pada ${info.day} ${monthName} berzodiak ${z.nameId} ${z.symbol} ` +
      `(unsur ${z.elementId}, dikuasai ${z.ruler}, periode ${z.rangeId}). ` +
      `Tahun ${info.year} adalah tahun shio ${info.china.zodiac} ${info.china.element} ${info.china.yinYang}.`,
  );

  // P3: events / hari penting
  if (liburParts.length || cutiParts.length || hariPentingNames.length) {
    const ev: string[] = [];
    if (liburParts.length) ev.push(`hari libur nasional (${liburParts.join(", ")})`);
    if (cutiParts.length) ev.push(`cuti bersama (${cutiParts.join(", ")})`);
    if (hariPentingNames.length) ev.push(`peringatan ${hariPentingNames.join(", ")}`);
    paragraphs.push(`Pada tanggal ini terdapat ${ev.join("; ")}.`);
  } else {
    paragraphs.push(
      `Tidak ada libur nasional atau cuti bersama yang ditetapkan pada tanggal ini` +
        (info.isSunday ? ", namun jatuh pada hari Minggu sehingga tetap menjadi tanggal merah." : "."),
    );
  }

  const faq: FaqItem[] = [
    {
      q: `Tanggal ${fullDate} hari apa?`,
      a: `${fullDate} adalah hari ${info.dayName}.`,
    },
    {
      q: `Apakah ${fullDate} tanggal merah?`,
      a: info.isTanggalMerah
        ? `Ya, ${fullDate} adalah tanggal merah${
            liburParts.length ? ` karena ${liburParts.join(", ")}` : info.isSunday ? " karena jatuh pada hari Minggu" : ""
          }.`
        : `Tidak, ${fullDate} bukan tanggal merah dan merupakan hari kerja biasa.`,
    },
    {
      q: `Zodiak orang yang lahir pada ${info.day} ${monthName} apa?`,
      a: `Orang yang lahir pada ${info.day} ${monthName} berzodiak ${z.nameId} (${z.nameEn}, ${z.symbol}). Unsurnya ${z.elementId}, dikuasai planet ${z.ruler}.`,
    },
    {
      q: `Shio tahun ${info.year} apa?`,
      a: `Tahun ${info.year} adalah tahun shio ${info.china.zodiac} dengan unsur ${info.china.element} ${info.china.yinYang}.`,
    },
  ];

  const about = [
    { "@type": "Date", name: info.iso },
    { "@type": "Thing", name: `Zodiak ${z.nameId}` },
    { "@type": "Thing", name: `Shio ${info.china.zodiac}` },
    { "@type": "Thing", name: `Weton ${info.jawa.weton}` },
  ];

  return { paragraphs, faq, about };
}

/* ─────────────────────────────────────────────── MONTH ── */

export function buildMonthSemantics(year: number, month: number): SemanticBlock {
  const monthName = MONTH_NAMES_ID[month - 1];
  const dim = daysInMonth(year, month);
  const holidays = getHolidaysForMonth(year, month);
  const libur = holidays.filter((h) => h.type === "libur");
  const cuti = holidays.filter((h) => h.type === "cuti-bersama");
  const hariPenting = getHariPentingForMonth(month);

  // working days (Mon-Fri minus libur nasional)
  let workdays = 0;
  let weekendDays = 0;
  let fridays = 0;
  for (let d = 1; d <= dim; d++) {
    const dow = dayOfWeek(year, month, d);
    if (dow === 0 || dow === 6) weekendDays++;
    else workdays++;
    if (dow === 5) fridays++;
  }
  const liburDates = new Set(libur.map((h) => h.date));
  let workdaysEffective = 0;
  for (let d = 1; d <= dim; d++) {
    const dow = dayOfWeek(year, month, d);
    if (dow === 0 || dow === 6) continue;
    const iso = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    if (!liburDates.has(iso)) workdaysEffective++;
  }

  // hijri months that begin in this gregorian month
  const hijriStarts: string[] = [];
  for (let d = 1; d <= dim; d++) {
    const h = gregorianToHijri(year, month, d);
    if (h.day === 1) hijriStarts.push(`${HIJRI_MONTHS[h.month - 1]} ${h.year} H`);
  }

  const paragraphs: string[] = [];
  paragraphs.push(
    `Bulan ${monthName} ${year} memiliki ${dim} hari, terdiri dari ${workdays} hari kerja kalender ` +
      `dan ${weekendDays} hari akhir pekan, dengan ${fridays} hari Jumat.`,
  );
  paragraphs.push(
    `Terdapat ${libur.length} hari libur nasional dan ${cuti.length} hari cuti bersama, sehingga jumlah hari kerja efektif diperkirakan ${workdaysEffective} hari.`,
  );
  if (hariPenting.length) {
    paragraphs.push(
      `Beberapa hari penting yang diperingati pada bulan ini: ${hariPenting
        .slice(0, 6)
        .map((h) => h.name)
        .join(", ")}${hariPenting.length > 6 ? ", dan lainnya" : ""}.`,
    );
  }
  if (hijriStarts.length) {
    paragraphs.push(`Bulan Hijriah yang dimulai dalam periode ini: ${hijriStarts.join(", ")}.`);
  }

  const faq: FaqItem[] = [
    {
      q: `Berapa jumlah hari di bulan ${monthName} ${year}?`,
      a: `Bulan ${monthName} ${year} memiliki ${dim} hari.`,
    },
    {
      q: `Berapa hari libur di bulan ${monthName} ${year}?`,
      a: `Terdapat ${libur.length} hari libur nasional${cuti.length ? ` dan ${cuti.length} cuti bersama` : ""} di bulan ${monthName} ${year}.`,
    },
    {
      q: `Berapa hari kerja efektif di bulan ${monthName} ${year}?`,
      a: `Diperkirakan ${workdaysEffective} hari kerja efektif (Senin–Jumat dikurangi libur nasional).`,
    },
  ];

  const about = [
    { "@type": "Thing", name: `${monthName} ${year}` },
    { "@type": "Thing", name: `Kalender Indonesia ${year}` },
  ];

  return { paragraphs, faq, about };
}

/* ─────────────────────────────────────────────── YEAR ── */

export function buildYearSemantics(year: number): SemanticBlock {
  const holidays = getHolidaysForYear(year);
  const libur = holidays.filter((h) => h.type === "libur");
  const cuti = holidays.filter((h) => h.type === "cuti-bersama");
  const hariPenting = getAllHariPentingForYear(year);
  const ch = gregorianToChinese(year, 6, 15); // mid-year shio is stable
  const hStart = gregorianToHijri(year, 1, 1);
  const hEnd = gregorianToHijri(year, 12, 31);

  let fridays = 0;
  let mondays = 0;
  for (let m = 1; m <= 12; m++) {
    const dim = daysInMonth(year, m);
    for (let d = 1; d <= dim; d++) {
      const dow = dayOfWeek(year, m, d);
      if (dow === 5) fridays++;
      if (dow === 1) mondays++;
    }
  }

  const paragraphs: string[] = [];
  paragraphs.push(
    `Tahun ${year} memiliki ${libur.length} hari libur nasional dan ${cuti.length} hari cuti bersama menurut SKB 3 Menteri, ` +
      `serta ${hariPenting.length} peringatan hari penting non-libur.`,
  );
  paragraphs.push(
    `Tahun ${year} bertepatan dengan tahun ${hStart.year} H – ${hEnd.year} H pada penanggalan Hijriah ` +
      `dan tahun shio ${ch.zodiac} ${ch.element} ${ch.yinYang} pada penanggalan Tionghoa.`,
  );
  paragraphs.push(
    `Sepanjang tahun terdapat ${fridays} hari Jumat dan ${mondays} hari Senin, ` +
      `yang berguna untuk perencanaan long weekend dan hari kejepit nasional (harpitnas).`,
  );

  const faq: FaqItem[] = [
    {
      q: `Berapa hari libur nasional tahun ${year}?`,
      a: `Tahun ${year} memiliki ${libur.length} hari libur nasional dan ${cuti.length} cuti bersama berdasarkan SKB 3 Menteri.`,
    },
    {
      q: `Tahun ${year} shio apa?`,
      a: `Tahun ${year} adalah tahun shio ${ch.zodiac} dengan unsur ${ch.element} ${ch.yinYang}.`,
    },
    {
      q: `Tahun ${year} bertepatan dengan tahun Hijriah berapa?`,
      a: `Tahun ${year} Masehi melingkupi tahun ${hStart.year} H sampai ${hEnd.year} H.`,
    },
  ];

  const about = [
    { "@type": "Thing", name: `Kalender Indonesia ${year}` },
    { "@type": "Thing", name: `Tahun ${ch.zodiac}` },
    { "@type": "Thing", name: `Tahun ${hStart.year} Hijriah` },
  ];

  return { paragraphs, faq, about };
}

/* ─────────────────────────────────────────────── SCHEMA ── */

export function buildFaqJsonLd(faq: FaqItem[]): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}
